import React, { useCallback } from 'react';

import { getOwnerPayoutsExport, IOwnerPayoutExport } from 'api/owner_sdk';
import dayjs from 'dayjs';
import { formatCurrency } from 'utils';

import { Button } from 'ui-kit';
import { downloadListOfListsAsCSV } from 'utils/csv';
import { BalanceSign } from 'api/types/balances';
import _ from 'lodash';

export interface IPayoutsExportButton {
  startDate: Date;
  endDate: Date;
}

const PayoutsExportButton = ({ startDate, endDate }: IPayoutsExportButton) => {
  const handleExport = useCallback(
    () =>
      getOwnerPayoutsExport({
        startDate,
        endDate,
      }).then((payouts: Array<IOwnerPayoutExport>) => {
        const payoutItems = [
          [
            'Store name',
            'Item',
            'Delivery date',
            'Item price',
            'Quantity',
            'Total (before fees and taxes)',
            'Refunded amount',
            'Merchant fees',
            'HostCo commission',
            'Your Profit',
            'Status',
          ],
          ...payouts.flatMap(({ store, order_products, order_products_are_paid }) =>
            order_products.map((order_product) => [
              store.store_name,
              order_product.product_name,
              dayjs(order_product.delivery_date).format('DD MMMM YYYY'),
              order_product.price,
              order_product.quantity,
              order_product.amount,
              order_product.amount_refunded,
              order_product.transaction_fee,
              order_product.hostco_profit,
              order_product.owner_profit,
              order_products_are_paid ? 'Completed' : 'Upcoming',
            ]),
          ),
          [
            'TOTALS',
            '',
            '',
            '',
            '',
            _.sumBy(payouts, (payout) =>
              _.sumBy(payout.order_products, (order_product) => Number(order_product.amount)),
            ),
            _.sumBy(payouts, (payout) =>
              _.sumBy(payout.order_products, (order_product) =>
                Number(order_product.amount_refunded),
              ),
            ),
            _.sumBy(payouts, (payout) =>
              _.sumBy(payout.order_products, (order_product) =>
                Number(order_product.transaction_fee),
              ),
            ),
            _.sumBy(payouts, (payout) =>
              _.sumBy(payout.order_products, (order_product) =>
                Number(order_product.hostco_profit),
              ),
            ),
            _.sumBy(payouts, (payout) =>
              _.sumBy(payout.order_products, (order_product) => Number(order_product.owner_profit)),
            ),
            '',
          ],
        ];

        const appliedBalances = payouts.flatMap(
          ({ store, currency, applied_balances }) =>
            applied_balances?.map((balance) => [
              store.store_name,
              balance.external_note,
              balance.sign === BalanceSign.DECREASE
                ? `-${formatCurrency(balance.amount, currency)}`
                : `${formatCurrency(balance.amount, currency)}`,
              dayjs(balance.date_created).format('DD MMMM YYYY'),
              'Yes',
            ]) || [],
        );

        const notAppliedBalances = payouts.flatMap(
          ({ store, currency, not_applied_balances }) =>
            not_applied_balances?.map((balance) => [
              store.store_name,
              balance.external_note,
              balance.sign === BalanceSign.DECREASE
                ? `-${formatCurrency(balance.leftover_amount, currency)}`
                : `${formatCurrency(balance.leftover_amount, currency)}`,
              '',
              'No',
            ]) || [],
        );

        const balances = [
          ['Store name', 'Note', 'Amount', 'Date created', 'Is applied'],
          ...appliedBalances,
          ...notAppliedBalances,
        ];

        const dataForCSV = [...payoutItems, [], ...balances];

        downloadListOfListsAsCSV({
          data: dataForCSV,
          filename: `Host Co Payouts.csv`,
        });
      }),
    [startDate, endDate],
  );

  return (
    <Button variant="contained" onClick={handleExport}>
      Export
    </Button>
  );
};

export default PayoutsExportButton;
